/**
 * Module-level state for the checkout flow.
 *
 * The Puck context in the browser is set from the server-rendered page.
 * When the user picks a shipping method or payment provider, the
 * wrapper's API call updates Medusa, but the browser's Puck context
 * stays stale until the next page render. Without a place to remember
 * the user's choice, components like OrderReview (which reads from
 * Puck context) would show the OLD value.
 *
 * These module-level state objects persist the user's most recent
 * choice across the entire page session. They are reset only on full
 * page navigation, which is exactly when the Puck context gets fresh
 * data from the server anyway.
 *
 * Both writer wrappers (ShippingMethod, PaymentMethod) and reader
 * wrappers (OrderReview) read/write these objects.
 */

import type { ShippingAddressValue } from '@launchstore/shared-puck';

export const checkoutSelections: {
  shippingId: string;
  shippingName: string;
  shippingPrice: string;
  /** Raw numeric shipping amount in minor units (cents). Lets the
   *  OrderTotals wrapper show a live shipping total even before the
   *  cart refetches and `cart.shipping_total` updates. */
  shippingAmount: number;
  shippingEstimate: string;
  paymentId: string;
  paymentDescription: string;
  /** Last shipping address submitted (set on Continue). Used by OrderReview
   *  to show the user-entered address even before the cart refetches. */
  address: ShippingAddressValue | null;
} = {
  shippingId: '',
  shippingName: '',
  shippingPrice: '',
  shippingAmount: 0,
  shippingEstimate: '',
  paymentId: '',
  paymentDescription: '',
  address: null,
};

/**
 * Step-completion predicates for the progressive step-by-step checkout.
 *
 * The single-page checkout shows only the next section whose prerequisites
 * are met. Step 1 (ShippingAddress) is always visible. Step 2 (Delivery)
 * shows once the address is saved. Step 3 (Payment) shows once a shipping
 * method is attached. Step 4 (Review) shows once a payment session is
 * pending.
 *
 * The cart from Puck context may be stale right after a save (the page
 * hasn't re-rendered yet), so each predicate also accepts an explicit
 * `fallback` (typically `checkoutSelections.*`) and returns true if EITHER
 * the cart says it's done OR the fallback is set.
 */
export const stepStatus = {
  /** Step 1 complete: the cart has a usable shipping address + email. */
  isAddressDone: (cart: any, fallback?: boolean) => {
    if (fallback) return true;
    const a = cart?.shipping_address;
    return !!(
      cart?.email &&
      a?.first_name &&
      a?.address_1 &&
      a?.city &&
      a?.country_code
    );
  },
  /** Step 2 complete: at least one shipping method attached to the cart. */
  isShippingDone: (cart: any, fallback?: boolean) => {
    if (fallback) return true;
    return (cart?.shipping_methods?.length ?? 0) > 0;
  },
  /** Step 3 complete: there's a pending payment session in the cart. */
  isPaymentDone: (cart: any, fallback?: boolean) => {
    if (fallback) return true;
    const sessions = cart?.payment_collection?.payment_sessions ?? [];
    return sessions.some((s: any) => s.status === 'pending');
  },

  /**
   * Cumulative "is this step accessible" predicates. Each step N is
   * accessible only if ALL prior steps (1..N-1) are done. This is the
   * correct gating for a single-page step-by-step checkout: even if the
   * cart has stale shipping methods or a pending payment session from a
   * previous test, a later step stays hidden until the user has walked
   * through the address form first.
   *
   * The cart from Puck context may be stale right after a save (the page
   * hasn't re-rendered yet), so each predicate also accepts explicit
   * fallbacks for the in-flight values from `checkoutSelections.*`. The
   * fallbacks are "the user has just saved step N" signals, not
   * independent completion proofs — the cart's own check is still
   * required to keep the fallback from masking a real regression.
   */
  isShippingAccessible: (
    cart: any,
    addressFallback?: boolean,
  ) => stepStatus.isAddressDone(cart, addressFallback),
  isPaymentAccessible: (
    cart: any,
    addressFallback?: boolean,
    shippingFallback?: boolean,
  ) =>
    stepStatus.isAddressDone(cart, addressFallback) &&
    stepStatus.isShippingDone(cart, shippingFallback),
  isReviewAccessible: (
    cart: any,
    addressFallback?: boolean,
    shippingFallback?: boolean,
    paymentFallback?: boolean,
  ) =>
    stepStatus.isAddressDone(cart, addressFallback) &&
    stepStatus.isShippingDone(cart, shippingFallback) &&
    stepStatus.isPaymentDone(cart, paymentFallback),
};
